import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, ShieldCheck } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { errMsg } from "../lib/api";
import { adminApi, ADMIN_TOKEN_KEY } from "../lib/adminApi";
import { AdminTournamentForm } from "../components/admin/AdminTournamentForm";
import { AdminTournamentDetail } from "../components/admin/AdminTournamentDetail";
import { AdminPasswordDialog } from "../components/admin/AdminPasswordDialog";
import { AdminPrivateList } from "../components/admin/AdminPrivateList";
import { AdminPrivateDetail } from "../components/admin/AdminPrivateDetail";

const AdminLogin = ({ onLogged }) => {
  const [f, setF] = useState({ username: "", password: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const r = await adminApi.post("/login", f);
      localStorage.setItem(ADMIN_TOKEN_KEY, r.data.token);
      onLogged(r.data.username);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <form onSubmit={submit} className="card-surface max-w-sm mx-auto mt-24 p-6 space-y-4" data-testid="admin-login-form">
      <h1 className="font-display text-3xl font-black uppercase flex items-center gap-2"><ShieldCheck className="text-emerald-400" /> Area riservata</h1>
      <Input placeholder="Utente" value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} required autoComplete="username" data-testid="admin-username-input" className="h-11 bg-[#0A0D14] border-[#26334D]" />
      <Input type="password" placeholder="Password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required autoComplete="current-password" data-testid="admin-password-input" className="h-11 bg-[#0A0D14] border-[#26334D]" />
      <Button type="submit" data-testid="admin-login-button" className="w-full h-11 rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#0A0D14] font-bold">Accedi</Button>
    </form>
  );
};

export default function AdminPage() {
  const [admin, setAdmin] = useState(undefined);
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [formKey, setFormKey] = useState(0);
  const [tab, setTab] = useState("public");
  const [privSel, setPrivSel] = useState(null);

  useEffect(() => {
    document.title = "Admin · Social Betting";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex,nofollow";
    document.head.appendChild(meta);
    if (!localStorage.getItem(ADMIN_TOKEN_KEY)) setAdmin(null);
    else adminApi.get("/me").then((r) => setAdmin(r.data.username)).catch(() => { localStorage.removeItem(ADMIN_TOKEN_KEY); setAdmin(null); });
    return () => meta.remove();
  }, []);

  const load = useCallback(() => adminApi.get("/tournaments").then((r) => setList(r.data)).catch((e) => e.response?.status === 401 && setAdmin(null)), []);
  useEffect(() => { if (admin) load(); }, [admin, load]);
  const logout = () => { localStorage.removeItem(ADMIN_TOKEN_KEY); setAdmin(null); };

  if (admin === undefined) return null;
  return (
    <div className="min-h-screen pitch-bg">
      {!admin ? <AdminLogin onLogged={setAdmin} /> : (
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 space-y-8" data-testid="admin-panel">
          <header className="flex items-center justify-between">
            <h1 className="font-display text-3xl sm:text-4xl font-black uppercase">Pannello <span className="text-emerald-400">admin</span></h1>
            <div className="flex items-center gap-4">
              <AdminPasswordDialog />
              <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-1.5 text-sm text-slate-300 hover:text-red-400"><LogOut size={16} /> Esci</button>
            </div>
          </header>
          <nav className="flex gap-2" data-testid="admin-tabs">
            {[["public", "Tornei pubblici"], ["private", "Tornei privati"]].map(([k, label]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
                className={`h-10 px-5 rounded-full text-sm font-bold transition-colors ${tab === k ? "bg-emerald-500 text-[#0A0D14]" : "border border-[#26334D] text-slate-300 hover:text-white"}`}>{label}</button>
            ))}
          </nav>
          {tab === "private" ? (
            privSel ? <AdminPrivateDetail tid={privSel} onBack={() => setPrivSel(null)} /> : <AdminPrivateList onSelect={setPrivSel} />
          ) : selected ? (
            <AdminTournamentDetail tid={selected} onBack={(changed) => { setSelected(null); if (changed) load(); }} />
          ) : (
            <div className="grid lg:grid-cols-12 gap-6">
              <section className="lg:col-span-7 space-y-3">
                <h2 className="font-display text-2xl font-bold uppercase">Tornei pubblici ({list.length})</h2>
                {list.length === 0 && <p className="card-surface p-6 text-slate-400">Nessun torneo pubblico. Creane uno qui accanto.</p>}
                {list.map((t) => (
                  <button key={t.id} onClick={() => setSelected(t.id)} data-testid={`admin-tournament-${t.id}`} className="card-surface w-full text-left p-4 hover:border-emerald-500/60 flex items-center justify-between gap-3">
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-400">{t.competition_name}</p>
                      <p className="font-display text-xl font-bold uppercase">{t.name}</p>
                    </div>
                    <span className="text-sm text-slate-400">{t.members_count} giocatori</span>
                  </button>
                ))}
              </section>
              <section className="lg:col-span-5 card-surface p-5">
                <h2 className="font-display text-2xl font-bold uppercase mb-4">Nuovo torneo pubblico</h2>
                <AdminTournamentForm key={formKey} onSaved={() => { load(); setFormKey((k) => k + 1); }} />
              </section>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
